import {useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {CircleAlert, CloudUpload, Info, Loader2, Plus, TriangleAlert, Workflow} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {Tabs, TabsList, TabsTrigger} from '@common-web/components/ui/tabs';
import {Tooltip, TooltipContent, TooltipTrigger} from '@common-web/components/ui/tooltip';
import {trpc} from '@common-web/lib/trpc';
import {cn} from '@common-web/lib/utils';
import {DetailHeader} from '@internal/components/DetailHeader';
import {EmptyTabState} from '@internal/pages/group-pipeline/components/EmptyTabState';
import {tenantColor} from '@internal/pages/group-pipeline/components/TenantTag';
import {
  ACTOR_OPTIONS,
  DisabledSelect,
  ReadOnly,
  STEP_GRID,
  TaskConfigRow,
} from '@internal/pages/group-pipeline/builder/TaskConfigRow';

const KIND_OPTIONS = [
  {value: 'INTAKE', label: 'Intake'},
  {value: 'GO_LIVE_PACKAGE', label: 'Go-live package'},
];

export function PipelineBuilderPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const config = trpc.internal.groupPipeline.config.useQuery();
  const {data: tenants, isLoading} = config;
  const [expanded, setExpanded] = useState<string[]>([]);

  const requested = searchParams.get('tenant');
  const tenant =
    tenants?.find((t) => t.shortName.toLowerCase() === requested?.toLowerCase()) ?? tenants?.[0];

  const pickTenant = (id: string) => {
    const next = tenants?.find((t) => t.id === id);
    if (next) setSearchParams({tenant: next.shortName.toLowerCase()}, {replace: true});
  };
  const toggle = (key: string) =>
    setExpanded((keys) => (keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]));

  const sections = tenant ? [...new Set(tenant.tasks.map((t) => t.section))] : [];

  return (
    <div className='mx-auto w-full max-w-7xl space-y-4 px-6 py-6'>
      <DetailHeader
        icon={<Workflow className='h-5 w-5' />}
        title='Pipeline Builder'
        subtitle='Each tenant’s task list for the group pipeline.'
      />

      {isLoading ? (
        <div className='py-16 text-center'>
          <Loader2 className='mx-auto h-6 w-6 animate-spin text-muted-foreground' />
        </div>
      ) : config.isError && !tenants ? (
        <EmptyTabState
          icon={CircleAlert}
          title='Couldn’t load the task lists'
          description='Something went wrong loading the configuration.'
          action={
            <Button
              variant='outline'
              className='bg-white'
              disabled={config.isFetching}
              onClick={() => void config.refetch()}
            >
              Retry
            </Button>
          }
        />
      ) : !tenant ? (
        <EmptyTabState
          icon={Workflow}
          title='No task lists yet'
          description='A tenant shows here once its task list is loaded.'
        />
      ) : (
        <>
          <Tabs value={tenant.id} onValueChange={pickTenant} noHash>
            <TabsList className='h-auto gap-1 p-1.5'>
              {tenants?.map((t) => (
                <TabsTrigger key={t.id} value={t.id} noHash className='gap-2.5 px-4 py-2 text-base'>
                  <span
                    className='h-2.5 w-2.5 shrink-0 rounded-full'
                    style={{backgroundColor: tenantColor(t.shortName)}}
                  />
                  {t.shortName}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <div className='rounded-lg border bg-white'>
            <div className='flex flex-wrap items-start justify-between gap-4 px-5 pt-4'>
              <p className='text-lg font-semibold'>{tenant.shortName} task list</p>
              <ReadOnly>
                <Button disabled>
                  <CloudUpload className='mr-1.5 h-4 w-4' />
                  Publish
                </Button>
              </ReadOnly>
            </div>
            <dl className='flex flex-wrap gap-x-10 gap-y-3 px-5 pb-4 pt-1'>
              {[
                [tenant.tasks.length, 'Steps'],
                [tenant.documents.length, 'Documents'],
                [tenant.groupsInPipeline, 'Groups in pipeline'],
              ].map(([value, label]) => (
                <div key={label} className='flex flex-col-reverse'>
                  <dt className='text-sm text-gray-500'>{label}</dt>
                  <dd className='text-2xl font-semibold'>{value}</dd>
                </div>
              ))}
            </dl>
            <p className='flex items-center gap-2 border-t px-5 py-3 text-sm text-gray-600'>
              <Info className='h-4 w-4 shrink-0 text-gray-400' />
              Changes apply to groups added afterward. Groups already in the pipeline keep the steps
              they started with.
            </p>
          </div>

          <div className='flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900'>
            <TriangleAlert className='h-4 w-4 shrink-0 text-amber-600' />
            <span>
              Read only in this first version.{' '}
              <span className='font-normal'>
                Added now to show the configuration options and how flexible each task list is.
              </span>
            </span>
          </div>

          {sections.map((section) => {
            const steps = tenant.tasks.filter((t) => t.section === section);
            return (
              <section key={section} className='overflow-hidden rounded-lg border bg-white'>
                <div className='flex items-center gap-2 border-b bg-gray-50 px-4 py-2.5'>
                  <span className='text-xs font-bold uppercase tracking-[0.07em]'>{section}</span>
                  <span className='text-sm text-gray-500'>{steps.length} steps</span>
                </div>
                <div className='overflow-x-auto'>
                  <div className='min-w-[960px]'>
                    <div
                      className={cn(
                        STEP_GRID,
                        'border-b px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500',
                      )}
                    >
                      <span />
                      <span>#</span>
                      <span>Step</span>
                      <ColumnHeading
                        label='Who acts'
                        info='Who does the work. Steps on the employer or broker get chase and response actions on Next move.'
                      />
                      <ColumnHeading
                        label='Due'
                        info='Business days from when the step opens. After that, the group shows as overdue.'
                      />
                      <ColumnHeading
                        label='Waits on'
                        info='Steps that must be done or skipped before this one opens.'
                      />
                      <span />
                    </div>
                    <ul>
                      {steps.map((task) => (
                        <TaskConfigRow
                          key={task.key}
                          task={task}
                          order={tenant.tasks.indexOf(task) + 1}
                          tasks={tenant.tasks}
                          sections={sections}
                          documents={tenant.documents}
                          expanded={expanded.includes(`${tenant.id}:${task.key}`)}
                          onToggle={() => toggle(`${tenant.id}:${task.key}`)}
                        />
                      ))}
                    </ul>
                  </div>
                </div>
                <div className='border-t p-3'>
                  <ReadOnly>
                    <Button variant='outline' disabled className='w-full bg-white'>
                      <Plus className='mr-1.5 h-4 w-4' />
                      Add step
                    </Button>
                  </ReadOnly>
                </div>
              </section>
            );
          })}

          <section className='overflow-hidden rounded-lg border bg-white'>
            <div className='flex items-center justify-between gap-2 border-b bg-gray-50 px-4 py-2'>
              <div className='flex items-center gap-2'>
                <span className='text-xs font-bold uppercase tracking-[0.07em]'>
                  Required documents
                </span>
                <span className='text-sm text-gray-500'>{tenant.documents.length}</span>
              </div>
              <ReadOnly>
                <Button variant='outline' size='sm' disabled className='bg-white'>
                  <Plus className='mr-1 h-3.5 w-3.5' />
                  Add document
                </Button>
              </ReadOnly>
            </div>
            <div className='overflow-x-auto'>
              <table className='w-full min-w-[760px] text-sm'>
                <thead className='border-b text-left text-xs uppercase tracking-wide text-gray-500'>
                  <tr>
                    <th className='px-4 py-2.5 font-semibold'>Document</th>
                    <th className='w-48 px-4 py-2.5 font-semibold'>Type</th>
                    <th className='w-48 px-4 py-2.5 font-semibold'>Provided by</th>
                    <th className='w-64 px-4 py-2.5 font-semibold'>Step</th>
                  </tr>
                </thead>
                <tbody>
                  {tenant.documents.map((doc) => (
                    <tr key={doc.key} className='border-b last:border-0'>
                      <td className='px-4 py-2.5 font-medium'>{doc.label}</td>
                      <td className='px-4 py-2.5'>
                        <ReadOnly>
                          <DisabledSelect value={doc.kind} options={KIND_OPTIONS} />
                        </ReadOnly>
                      </td>
                      <td className='px-4 py-2.5'>
                        <ReadOnly>
                          <DisabledSelect value={doc.actor} options={ACTOR_OPTIONS} />
                        </ReadOnly>
                      </td>
                      <td className='px-4 py-2.5'>
                        <ReadOnly>
                          <DisabledSelect
                            value={doc.taskKey ?? ''}
                            options={tenant.tasks.map((t) => ({value: t.key, label: t.label}))}
                          />
                        </ReadOnly>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function ColumnHeading({label, info}: {label: string; info: string}) {
  return (
    <span className='flex items-center gap-1.5'>
      {label}
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            aria-label={info}
            className='inline-flex cursor-help text-gray-400 hover:text-gray-900'
          >
            <Info className='h-3.5 w-3.5 -translate-y-px' />
          </span>
        </TooltipTrigger>
        <TooltipContent className='max-w-64 bg-gray-900 normal-case tracking-normal text-white'>
          {info}
        </TooltipContent>
      </Tooltip>
    </span>
  );
}
